<template lang="pug">
v-card
  v-card-title
    .subtitle-1 Print PDF

  v-card-text
    //- Component selection
    v-label
      strong Which Components?
    v-divider

    component-selector(:game="game")

    //- Layout Modes
    v-label
      strong Layout Mode
    v-divider

    v-radio-group(v-model="printMode")
      v-tooltip(bottom)
        template(v-slot:activator="{ on }")
          span(v-on="on")
            v-radio(label="Auto Layout" :value="MODE_AUTO_LAYOUT")
        span This is for most users who want to specify their paper size and have PapArtisan lay everything out.

      v-tooltip(bottom)
        template(v-slot:activator="{ on }")
          span(v-on="on")
            v-radio(label="Tabletop Simulator" :value="MODE_TABLETOP_SIMULATOR")
        span 25x24.5 with 0 margins, this setting is ideal for users looking to convert their PDF to a PNG and then feeding it to the Tabletop Simulator Deck Editor.

      v-tooltip(bottom)
        template(v-slot:activator="{ on }")
          span(v-on="on")
            v-radio(label="Component Per Page" :value="MODE_COMPONENT_PER_PAGE")
        p This allows advanced users to do their own layout.
        span PapArtisan will place each component onto a page of its own, sized exactly to the component with no margins. Advanced users can then do their own layout.


    template(v-if="printMode == MODE_AUTO_LAYOUT")
      //- Paper Size
      v-label
        strong Paper Size
      v-divider

      div
        v-btn-toggle(v-model="paperMode")
          v-btn(text value="standard") Standard Sizes
          v-btn(text value="custom") Custom Size

      //- Standard Sizes
      div(v-if="paperMode != 'custom'")
        p Most home printers use A4 or Letter paper. Select "Universal" if you'd like a print that will work on either.

        v-select(dense outlined label="Size" :items="printOptions" item-text="name" item-value="value" v-model="paper")
        v-select(dense outlined label="Orientation" :items="orientationOptions" item-text="name" item-value="value" v-model="orientation")

      //- Custom Size
      template(v-else)
        p Set your own custom page size in inches.

        v-text-field(dense outlined v-model.number="paperWidth" label="Width" suffix="in." type="number" step ="0.01" min="0")
        v-text-field(dense outlined v-model.number="paperHeight" label="Height" suffix="in." type="number" step ="0.01" min="0")


      v-label
        strong Component Spacing
      v-divider

      v-checkbox(label="Enable Spacing?" v-model="componentSpacing")

      v-label
        strong Merge same size components
      v-divider
        p If you want to print same size components in the same page instead of starting a new one.
      v-checkbox(label="Merge components?" v-model="componentMerging")


      //- Margins
      v-label
        strong Minimum Margins
      v-divider

      p How close to the paper's edge will your printer allow you to print?

      p
        v-btn(text @click="clearMargins") Clear
        v-btn(text @click="defaultMargins") Defaults

      v-text-field(dense outlined v-model.number="marginTop" label="Top" suffix="in." type="number" step ="0.01" min="0")
      v-text-field(dense outlined v-model.number="marginLeft" label="Left" suffix="in." type="number" step ="0.01" min="0")
      v-text-field(dense outlined v-model.number="marginRight" label="Right" suffix="in." type="number" step ="0.01" min="0")
      v-text-field(dense outlined v-model.number="marginBottom" label="Bottom" suffix="in." type="number" step ="0.01" min="0")

  v-card-actions
    v-spacer
    v-btn(text @click="$emit('close-dialog')") Cancel
    v-btn(color="primary" @click="confirm")
      v-icon(left small) photo_library
      | Print PDF
</template>

<script>
import { capitalize, debounce, map } from 'lodash'
import { mapGetters } from 'vuex'
import { MODE_AUTO_LAYOUT, MODE_TABLETOP_SIMULATOR, MODE_COMPONENT_PER_PAGE, PAGE_DIMENSIONS,
  ORIENTATIONS } from '../../store/print'
import ComponentSelector from './ComponentSelector.vue'

const
  printOptions = map(PAGE_DIMENSIONS, (dimension, key) => {
    return { ...dimension, value: key}
  }),

  orientationOptions = map(ORIENTATIONS, (orientation) => {
    return { name: capitalize(orientation), value: orientation }
  })

export default {
  components: { ComponentSelector },

  props: ['game'],

  data() {
    return {
      MODE_AUTO_LAYOUT,
      MODE_TABLETOP_SIMULATOR,
      MODE_COMPONENT_PER_PAGE,
      printOptions,
      orientationOptions,
    }
  },

  computed: {
    ...mapGetters(['getPrintSettings']),

    printMode: {
      get() { return this.getPrintSettings.mode },
      set(mode) { this.updatePrintSettings({ mode }) },
    },

    paperMode: {
      get() { return this.paper === 'custom' ? 'custom' : 'standard' },
      set(mode) { this.paper = mode === 'custom' ? 'custom' : 'universal' },
    },

    paper: {
      get() { return this.getPrintSettings.paper },
      set(paper) { this.updatePrintSettings({ paper }) },
    },

    orientation: {
      get() { return this.getPrintSettings.orientation },
      set(orientation) { this.updatePrintSettings({ orientation }) },
    },

    paperWidth: {
      get() { return this.getPrintSettings.width },
      set(width) { this.updatePrintSettings({ customWidth: width }) },
    },

    paperHeight: {
      get() { return this.getPrintSettings.height },
      set(height) { this.updatePrintSettings({ customHeight: height }) },
    },

    componentSpacing: {
      get() { return this.getPrintSettings.componentSpacing },
      set(componentSpacing) { this.updatePrintSettings({ componentSpacing }) },
    },

    componentMerging: {
      get() { return this.getPrintSettings.componentMerging },
      set(componentMerging) { this.updatePrintSettings({ componentMerging }) },
    },

    marginTop: {
      get() { return this.getPrintSettings.marginTop },
      set(marginTop) { this.updatePrintSettings({ marginTop }) },
    },

    marginRight: {
      get() { return this.getPrintSettings.marginRight },
      set(marginRight) { this.updatePrintSettings({ marginRight }) },
    },

    marginBottom: {
      get() { return this.getPrintSettings.marginBottom },
      set(marginBottom) { this.updatePrintSettings({ marginBottom }) },
    },

    marginLeft: {
      get() { return this.getPrintSettings.marginLeft },
      set(marginLeft) { this.updatePrintSettings({ marginLeft }) },
    },
  },

  methods: {
    updatePrintSettings: debounce(function(attributes) {
      this.$store.dispatch('updatePrintSettings', attributes)
    }, 200),

    clearMargins() {
      this.$store.dispatch('updatePrintSettings', { marginTop: 0, marginBottom: 0, marginRight: 0, marginLeft: 0 })
    },

    defaultMargins() {
      this.$store.dispatch('updatePrintSettings', { marginTop: .25, marginBottom: .25, marginRight: .25, marginLeft: .25 })
    },

    confirm() {
      this.$emit('confirm')
      this.$emit('close-dialog')
    },
  },
}
</script>

<style scoped>
.component-selection {
  margin-bottom: 1em;
}

.component-selection .v-card__text {
  padding: .5em;
}

.v-input--checkbox:first-child {
  margin-top: 0;
}
</style>
